import { useEffect, useState } from "react";
import { Check, Copy, Send, Zap } from "react-feather";
import TypingIndicator from "./typing_indicator";
import BotPromptSuggestions from "./bot_prompt_suggestionns";

const promptQuestions = [
  "What is anti fraud policy?",
  "My app is not working, what can I do?",
  "What is CMS?",
];

const MESSAGE_STORAGE_KEY = "chatbot_messages";
const LAST_MESSAGE_TIME_KEY = "chatbot_lastMessageTime";

function MessageBubble({
  message,
  onCopy,
}: {
  message: any;
  onCopy: (text: string) => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(message.text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy(message.text);
  };

  // Format text with basic markdown support
  const formatText = (text: string) => {
    if (!text) return null;

    const lines = text.split("\n");
    return lines.map((line, i) => {
      // Bold text
      line = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      // Italic text
      line = line.replace(/\*(.*?)\*/g, "<em>$1</em>");
      // Code inline
      line = line.replace(
        /`(.*?)`/g,
        '<code class="bg-gray-700 px-1 rounded text-sm">$1</code>'
      );

      return (
        <div
          key={i}
          dangerouslySetInnerHTML={{ __html: line }}
          className={line.trim() ? "mb-2 last:mb-0" : "mb-1"}
        />
      );
    });
  };

  if (message.id === "typing-indicator") {
    return (
      <div className="flex items-center space-x-2 text-gray-400 text-sm">
        <span>Agent is typing</span>
        <div className="flex space-x-1">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 bg-green-500 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex w-full ${
        message.role === "user" ? "justify-end" : "justify-start"
      }`}
    >
      <div
        className={`group relative flex flex-row gap-1 max-w-[80%] ${
          message.role === "user" ? "ml-12" : "mr-12"
        }`}
      >
        {message.role === "assistant" && (
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg text-white">
            <Zap height={15} width={13} />
          </div>
        )}
        <div
          className={`rounded-2xl px-3 py-1 shadow-lg transition-all ${
            message.role === "user"
              ? "bg-green-600 text-white rounded-tr-sm"
              : "bg-gray-800 text-gray-100 rounded-tl-sm border border-gray-700"
          }`}
        >
          <div className="text-sm leading-relaxed select-text">
            {formatText(message.text)}
          </div>
        </div>

        {message.role === "assistant" && message.id !== "typing-indicator" && (
          <button
            onClick={handleCopy}
            className="absolute -right-7 top-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-700 rounded-lg"
            title="Copy message"
          >
            {copied ? (
              <Check className="h-3 w-3 text-green-500" />
            ) : (
              <Copy className="h-3 w-3 text-gray-400" />
            )}
          </button>
        )}
      </div>
    </div>
  );
}

function BotChatWindow({
  scrollRef,
  inputRef,
  expanded,
}: {
  scrollRef: React.RefObject<HTMLDivElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  expanded: boolean;
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  const [showPrompts, setShowPrompts] = useState<boolean>(() => {
    const chatBotStarted = localStorage.getItem("chatbot_hasChatted");
    const storedTime = localStorage.getItem(LAST_MESSAGE_TIME_KEY);

    if (chatBotStarted && storedTime) {
      const lastTime = parseInt(storedTime, 10);
      const now = Date.now();
      if (now - lastTime < 10 * 60 * 1000) return false; // chat ongoing
      localStorage.removeItem(MESSAGE_STORAGE_KEY);
      localStorage.removeItem(LAST_MESSAGE_TIME_KEY);
      localStorage.removeItem("chatbot_hasChatted");
    }
    return true; // show prompts if first visit or expired
  });

  const [isLoading, setIsLoading] = useState(false);
  const [input, setInput] = useState("");

  const [messages, setMessages] = useState<
    Array<{ id: string; role: "user" | "assistant"; text: string }>
  >(() => {
    try {
      const storedMessages = localStorage.getItem(MESSAGE_STORAGE_KEY);
      const storedTime = localStorage.getItem(LAST_MESSAGE_TIME_KEY);
      if (storedMessages && storedTime) {
        const parsedMessages = JSON.parse(storedMessages);
        const lastTime = parseInt(storedTime, 10);
        const now = Date.now();
        if (now - lastTime < 10 * 60 * 1000) return parsedMessages;
      }
    } catch {
      // Ignore, will reset
    }
    return [
      {
        id: crypto.randomUUID(),
        role: "assistant",
        text: "Hi! I am Kazam Agent. How can I help you?",
      },
    ];
  });

  // Update chatOpen based on expanded
  useEffect(() => {
    setChatOpen(expanded);
  }, [expanded]);

  // Send message
  const sendMessage = async (textArg?: string) => {
    const text = (textArg ?? input).trim();
    if (!text) return;

    localStorage.setItem("chatbot_hasChatted", "true");

    const userMsg = { id: crypto.randomUUID(), role: "user" as const, text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    setIsLoading(true);

    const typingId = crypto.randomUUID();
    const typingMsg = {
      id: typingId,
      role: "assistant" as const,
      text: "typing...",
    };
    setMessages((prev) => [...prev, typingMsg]);

    try {
      const url = "https://erp-bot.kazam.in/erp-chat";
      const token = localStorage.getItem("token");
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          bearerToken: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: text }),
      });

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      const formattedText =
        data?.response?.replace(/\\n/g, "\n") ?? "No response received";

      const assistantMsg = {
        id: crypto.randomUUID(),
        role: "assistant" as const,
        text: formattedText,
      };
      setMessages((prev) =>
        prev.filter((m) => m.id !== typingId).concat(assistantMsg)
      );
    } catch (error) {
      console.error("Error in erpChat API call:", error);
      const errorMsg = {
        id: crypto.randomUUID(),
        role: "assistant" as const,
        text: "Sorry, I encountered an error. Please try again.",
      };
      setMessages((prev) =>
        prev.filter((m) => m.id !== typingId).concat(errorMsg)
      );
    }

    setIsLoading(false);
    inputRef.current?.focus();
  };

  // Persist chat
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(MESSAGE_STORAGE_KEY, JSON.stringify(messages));
      localStorage.setItem(LAST_MESSAGE_TIME_KEY, Date.now().toString());
    }
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  useEffect(() => {
    let clearTimeoutId: NodeJS.Timeout | null = null;

    if (!chatOpen) {
      const lastTimeStr = localStorage.getItem(LAST_MESSAGE_TIME_KEY);
      if (!lastTimeStr) return;

      const lastTime = parseInt(lastTimeStr, 10);
      const remainingTime = 10 * 60 * 1000 - (Date.now() - lastTime);

      if (remainingTime > 0) {
        clearTimeoutId = setTimeout(() => {
          localStorage.removeItem(MESSAGE_STORAGE_KEY);
          localStorage.removeItem(LAST_MESSAGE_TIME_KEY);
          localStorage.removeItem("chatbot_hasChatted");
          setMessages([
            {
              id: crypto.randomUUID(),
              role: "assistant",
              text: "Hi! I am Kazam Agent. How can I help you?",
            },
          ]);
          setShowPrompts(true);
        }, remainingTime);
      } else {
        // already expired, clear immediately if chat closed
        localStorage.removeItem(MESSAGE_STORAGE_KEY);
        localStorage.removeItem(LAST_MESSAGE_TIME_KEY);
        localStorage.removeItem("chatbot_hasChatted");
        setMessages([
          {
            id: crypto.randomUUID(),
            role: "assistant",
            text: "Hi! I am Kazam Agent. How can I help you?",
          },
        ]);
        setShowPrompts(true);
      }
    }

    return () => {
      if (clearTimeoutId) {
        clearTimeout(clearTimeoutId); // only clear if assigned
      }
    };
  }, [chatOpen, messages]);

  // Detect mobile
  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const handlePromptClick = (question: string) => {
    setShowPrompts(false);
    sendMessage(question);
  };

  const handleCopy = (text: string) => {
    console.log("Copied:", text);
  };

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto relative">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-gray-950 z-0"></div>

      <div
        ref={scrollRef}
        style={{
          height: expanded ? "calc(100% - 50px)" : "calc(100% - 130px)",
        }}
        className="flex flex-col space-y-3 p-3 overflow-y-auto"
        aria-live="polite"
        aria-busy={false}
      >
        <div className="relative z-10 flex flex-col space-y-3">
          {showPrompts && messages.length === 1 && (
            <BotPromptSuggestions
              prompts={promptQuestions}
              onSelect={handlePromptClick}
            />
          )}
          {messages.map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              onCopy={handleCopy}
            />
          ))}
        </div>
      </div>

      <form
        className="border-t border-gray-600 px-2 py-3 bg-gray-900 backdrop-blur-sm bottom-1.5 relative z-10"
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
      >
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            value={input}
            disabled={isLoading}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message…"
            className={`h-10 flex-1 rounded-xl border border-gray-600 bg-gray-900 bg-opacity-80 backdrop-blur-sm text-white placeholder-gray-400 px-3 text-sm outline-none focus:ring-2 focus:ring-green-400 focus-within ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          />
          <button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="inline-flex items-center gap-2 rounded-xl bg-green-600 px-4 py-3 text-sm font-medium text-white shadow-lg transition-all hover:bg-green-700 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-green-600"
          >
            <Send className="h-4 w-4" />
            <span className="hidden sm:inline">Send</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default BotChatWindow;
